/**
 * TargetComparisonViz - Multi-target comparison radar chart for chat mode.
 *
 * Compares multiple lncRNA targets across key metrics: specificity, network score,
 * druggability, expression, and literature evidence.
 */

import React from 'react';
import Plot from 'react-plotly.js';
import InfoTooltip from './InfoTooltip';

interface TargetComparisonVizProps {
  data: {
    type: 'comparison';
    targets: Array<{
      lncrna: string;
      specificity_score?: number;
      network_score?: number;
      druggability_score?: number;
      expression_score?: number;
      literature_score?: number;
      overall_score?: number;
    }>;
    metadata?: {
      disease: string;
    };
  };
  onAction?: (action: string, params?: any) => void;
}

export const TargetComparisonViz: React.FC<TargetComparisonVizProps> = ({ data, onAction }) => {
  if (!data.targets || data.targets.length === 0) {
    return (
      <div className="origin-card mt-4 p-4">
        <p className="text-sm text-gray-500">No targets to compare.</p>
      </div>
    );
  }

  // Define radar chart axes
  const categories = [
    'Specificity',
    'Network',
    'Druggability',
    'Expression',
    'Literature'
  ];

  // Color palette for up to 5 targets
  const colors = [
    'rgba(54, 162, 235, 0.5)',
    'rgba(255, 99, 132, 0.5)',
    'rgba(75, 192, 192, 0.5)',
    'rgba(255, 206, 86, 0.5)',
    'rgba(153, 102, 255, 0.5)'
  ];

  // Prepare data for each target
  const radarData: any[] = data.targets.map((target, idx) => ({
    type: 'scatterpolar',
    r: [
      target.specificity_score || 0,
      target.network_score || 0,
      target.druggability_score || 0,
      target.expression_score || 0,
      target.literature_score || 0
    ],
    theta: categories,
    fill: 'toself',
    fillcolor: colors[idx % colors.length],
    line: { color: colors[idx % colors.length].replace('0.5', '1.0') },
    name: target.lncrna,
    hovertemplate: '<b>%{fullData.name}</b><br>%{theta}: %{r:.1f}<extra></extra>'
  }));

  return (
    <div className="origin-card mt-4 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-1">Target Comparison</h3>
        {data.metadata?.disease && (
          <p className="text-sm text-gray-400">{data.metadata.disease}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Comparing {data.targets.length} target{data.targets.length > 1 ? 's' : ''}
        </p>
      </div>

      {/* Radar Chart */}
      <Plot
        data={radarData}
        layout={{
          polar: {
            radialaxis: {
              visible: true,
              range: [0, 100],
              ticksuffix: '',
              gridcolor: 'rgba(75, 85, 99, 0.5)',
              color: '#9ca3af',
            },
            angularaxis: {
              gridcolor: 'rgba(75, 85, 99, 0.5)',
              color: '#d1d5db',
            },
            bgcolor: 'transparent',
          },
          height: 450,
          paper_bgcolor: 'transparent',
          font: { color: '#d1d5db' },
          showlegend: true,
          legend: {
            x: 1,
            xanchor: 'right',
            y: 1,
            font: { color: '#d1d5db' },
          },
          margin: { l: 80, r: 120, t: 40, b: 80 }
        }}
        config={{ responsive: true, displayModeBar: false }}
      />

      {/* Score Table */}
      <div className="mt-4">
        <h4 className="text-sm font-semibold mb-2">Score Breakdown</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="text-left py-2 px-3">Target</th>
                <th className="text-right py-2 px-2">
                  <span className="inline-flex items-center justify-end gap-1">
                    Specificity
                    <InfoTooltip title="Specificity" content="How disease-specific the lncRNA's regulatory activity is (0–100). Higher = more rewiring unique to disease vs normal tissue." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-2">
                  <span className="inline-flex items-center justify-end gap-1">
                    Network
                    <InfoTooltip title="Network" content="Size and connectivity of the gene regulatory network (0–100). Higher = larger, more connected network with more potential drug targets." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-2">
                  <span className="inline-flex items-center justify-end gap-1">
                    Druggability
                    <InfoTooltip title="Druggability" content="Predicted tractability for therapeutic targeting (0–100). Based on FDA-approved targets in the network and ASO design potential." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-2">
                  <span className="inline-flex items-center justify-end gap-1">
                    Expression
                    <InfoTooltip title="Expression" content="Magnitude of dysregulation in disease context (0–100). Derived from Log2FC and statistical significance across cell types." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-2">
                  <span className="inline-flex items-center justify-end gap-1">
                    Literature
                    <InfoTooltip title="Literature" content="Volume and relevance of published evidence (0–100). Based on PubMed citation count and disease-specific mentions." position="right" />
                  </span>
                </th>
                {data.targets[0].overall_score !== undefined && (
                  <th className="text-right py-2 px-3 font-semibold">
                    <span className="inline-flex items-center justify-end gap-1">
                      Overall
                      <InfoTooltip title="Overall Score" content="Weighted composite of all 5 axes (Specificity, Network, Druggability, Expression, Literature). Primary ranking metric." position="right" />
                    </span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.targets.map((target, idx) => (
                <tr key={idx} className="border-b border-gray-700 hover:bg-gray-800">
                  <td className="py-2 px-3 font-mono font-semibold">{target.lncrna}</td>
                  <td className="py-2 px-2 text-right font-mono">
                    {target.specificity_score?.toFixed(1) || '-'}
                  </td>
                  <td className="py-2 px-2 text-right font-mono">
                    {target.network_score?.toFixed(1) || '-'}
                  </td>
                  <td className="py-2 px-2 text-right font-mono">
                    {target.druggability_score?.toFixed(1) || '-'}
                  </td>
                  <td className="py-2 px-2 text-right font-mono">
                    {target.expression_score?.toFixed(1) || '-'}
                  </td>
                  <td className="py-2 px-2 text-right font-mono">
                    {target.literature_score?.toFixed(1) || '-'}
                  </td>
                  {target.overall_score !== undefined && (
                    <td className="py-2 px-3 text-right font-mono font-semibold">
                      {target.overall_score.toFixed(1)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action buttons */}
      {onAction && (
        <div className="mt-4 flex flex-wrap gap-2">
          {data.targets.map((target, idx) => (
            <button
              key={idx}
              onClick={() => onAction('view_target_details', { lncrna: target.lncrna })}
              className="origin-btn origin-btn-sm origin-btn-secondary"
            >
              View {target.lncrna}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TargetComparisonViz;
